import { ReactNode } from 'react';
import { Event as BlipEvent } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records-ext/call-quality/v1/api.pb';
import dayjs from 'dayjs';
import { Chart, ScatterChartAppearance, ScatterChartData } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Heading, Text } from '@frontend/design-system';
import { CALL_QUALITY } from './const';
import { DotWithLabel, TooltipDetailRow } from './timeline-chart';

export const OverviewSection = ({
  subtitle,
  quality,
  mos,
  duration,
  eventCount,
}: {
  subtitle: string;
  quality: (typeof CALL_QUALITY)[keyof typeof CALL_QUALITY];
  mos: string;
  duration: string;
  eventCount: number;
}) => {
  const { t } = useTranslation('calls');
  const heading = t('Overview');

  const items = [
    {
      title: t('Call Quality'),
      label:
        quality === CALL_QUALITY.GOOD ? (
          <DotWithLabel label={t('High')} color={theme.colors.success20} />
        ) : (
          <DotWithLabel label={t('Low')} color={theme.colors.critical30} />
        ),
    },
    {
      title: t('MOS (avg.)'),
      label: <Text weight='bold'>{mos}</Text>,
    },
    {
      title: t('Duration'),
      label: <Text weight='bold'>{duration}</Text>,
    },
    {
      title: t('Events'),
      label: <Text weight='bold'>{eventCount}</Text>,
    },
  ];

  return (
    <div
      css={{
        display: 'grid',
        gap: theme.spacing(2),
        alignItems: 'center',
      }}
    >
      <div>
        <Heading level={3}>{heading}</Heading>
        <Text as='span' size='medium'>
          {subtitle}
        </Text>
      </div>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(0px, 1fr))',
          gridTemplateRows: 60,
          gap: theme.spacing(1),
        }}
      >
        {items.map((item) => (
          <OverviewBox key={item.title} title={item.title} label={item.label} />
        ))}
      </div>
    </div>
  );
};

const OverviewBox = ({ title, label }: { title: string; label: ReactNode }) => {
  return (
    <div
      css={{
        display: 'grid',
        border: `1px solid ${theme.colors.neutral20}`,
        borderRadius: theme.borderRadius.medium,
        padding: theme.spacing(1),
      }}
    >
      <Text size='small' color='light'>
        {title}
      </Text>
      {label}
    </div>
  );
};

export const MosChartSection = ({ subtitle, blipEvents }: { subtitle: string; blipEvents: BlipEvent[] }) => {
  const { t } = useTranslation('calls');
  const heading = t('MOS');

  const data = generateChartData(blipEvents);

  return (
    <div
      css={{
        display: 'grid',
        gap: theme.spacing(2),
        alignItems: 'center',
      }}
    >
      <div>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: 'max-content max-content',
            alignItems: 'center',
            gap: theme.spacing(1),
          }}
        >
          <Heading level={3}>{heading}</Heading>
          <Button size='small' iconName='info-small' variant='secondary' />
        </div>
        <Text as='span' size='medium'>
          {subtitle}
        </Text>
        <Chart
          noBorder
          colors={{
            mos: theme.colors.critical30,
          }}
          labels={{
            mos: 'MOS',
          }}
          strokeColors={{
            mos: theme.colors.critical50,
          }}
        >
          <Chart.ScatterChart
            appearance={{
              ...appearance,
              xAxis: {
                ...appearance.xAxis,
                ticks: [data.groups[0].values[0].x, data.groups[0].values.at(-1)?.x ?? data.groups[0].values[0].x],
              },
            }}
            data={data}
            tooltipRenderer={(props) => {
              const title = props.payload?.[0]?.value;
              const label = props.payload?.[1]?.value;
              return (
                <div
                  css={{
                    display: 'grid',
                    gap: theme.spacing(1),
                    padding: theme.spacing(3),
                    fontWeight: 'normal',
                    background: theme.colors.white,
                    minWidth: theme.spacing(12.5),
                  }}
                >
                  <Text as='span' size='small'>
                    {typeof title === 'number' || typeof title === 'string' ? dayjs(title).format('h:mm:ss A') : ''}
                  </Text>
                  <TooltipDetailRow
                    title={<TooltipLabel type='value' />}
                    label={
                      <span>
                        <Text
                          as='span'
                          size='medium'
                          weight='bold'
                          color={label && typeof label === 'number' && label < 3.5 ? 'error' : 'default'}
                        >
                          {label?.toString() ?? ''}{' '}
                        </Text>
                        <Text as='span' size='medium' weight='bold'>
                          / 5
                        </Text>
                      </span>
                    }
                  />
                  <TooltipDetailRow title={<TooltipLabel type='threshold' />} label={t('> 3.5')} />
                </div>
              );
            }}
          />
        </Chart>
      </div>
    </div>
  );
};

const TooltipLabel = ({
  type,
  textColor = 'default',
}: {
  type: 'value' | 'threshold';
  textColor?: 'default' | 'light';
}) => {
  const { t } = useTranslation('calls');
  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: '24px max-content',
        gap: theme.spacing(2),
        alignItems: 'center',
      }}
    >
      {type === 'value' ? (
        <div
          css={{
            justifySelf: 'center',
            borderRadius: '50%',
            background: theme.colors.critical30,
            width: 12,
            height: 12,
            outline: `1px solid ${theme.colors.critical50}`,
          }}
        />
      ) : (
        <div css={{ borderTop: `2px dashed ${theme.colors.neutral40}`, width: 24 }} />
      )}
      <Text as='span' size='medium' color={textColor}>
        {type === 'value' ? t('MOS') : t('Recommended MOS')}
      </Text>
    </div>
  );
};

function generateChartData(events: BlipEvent[]) {
  const data: ScatterChartData = {
    groups: [
      {
        name: 'mos',
        values: events.map((event) => ({
          x: new Date(event.eventTime).getTime(),
          y: event.mosScore,
          size: 6,
        })),
      },
    ],
    markers: [
      {
        id: 'mos_avg',
        value: 3.5,
        color: theme.colors.neutral50,
      },
    ],
  };

  return data;
}

const appearance: ScatterChartAppearance = {
  customXAxisTickFormat: (value) => {
    return dayjs(value).format('h:mm:ss A');
  },
  showXAxis: true,
  showYAxis: true,
  strokeWidth: 1,
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
  showGridLines: true,
  hideMarkerEnds: true,
  gridBackgroundColor: theme.colors.neutral5,
  yAxis: {
    domain: [0, 5],
    tickCount: 5,
    scale: 'linear',
    width: 20,
  },
  xAxis: {
    tickCount: 3,
    padding: {
      left: 30,
      right: 40,
    },
    tickLine: {
      stroke: theme.colors.neutral20,
      strokeWidth: 1,
    },
  },
};
